import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, CheckCircle2, Repeat, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtBRL, extractError } from "@/lib/api";
import { DesativarRecorrenciaButton } from "@/components/financeiro/DesativarRecorrenciaButton";

const hojeISO = () => new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });

export function DespesaRow({ d, onDelete, onChanged }) {
  const [pagarOpen, setPagarOpen] = useState(false);
  const [dataPag, setDataPag] = useState(hojeISO());
  const [busy, setBusy] = useState(false);
  const pago = d.status === "PAGO";

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChanged(); }
    catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const pagar = () => run(async () => {
    await api.patch(`/financeiro/despesas/${d.id}/pagar`, { data_pagamento: dataPag });
    setPagarOpen(false);
  }, "Despesa marcada como paga");

  const reabrir = () => run(() => api.patch(`/financeiro/despesas/${d.id}/pendente`), "Despesa reaberta como pendente");

  return (
    <div className="flex items-center justify-between gap-3 border border-border/60 p-3 rounded-sm flex-wrap" data-testid={`despesa-row-${d.id}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium">{d.descricao}</div>
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          <Badge variant="outline" className={`text-[9px] uppercase tracking-widest ${d.categoria === "VARIAVEL" ? "border-info/40 text-info" : "border-warning/40 text-warning"}`}>
            {d.categoria}
          </Badge>
          <Badge data-testid={`despesa-status-${d.id}`} variant="outline"
            className={`text-[9px] uppercase tracking-widest ${pago ? "border-success/40 text-success" : "border-destructive/40 text-destructive"}`}>
            {pago ? `Pago${d.data_pagamento ? ` · ${d.data_pagamento.slice(0, 10).split("-").reverse().join("/")}` : ""}` : "Pendente"}
          </Badge>
          {d.recorrencia_id && (
            <Badge data-testid={`despesa-recorrente-${d.id}`} variant="outline"
              className={`text-[9px] uppercase tracking-widest gap-1 ${d.recorrencia_ativa ? "border-info/40 text-info" : "text-muted-foreground"}`}>
              <Repeat size={9} /> {d.recorrencia_ativa ? "Mensal" : "Recorrência desativada"}
            </Badge>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="stat-num text-lg mr-2" data-testid={`despesa-valor-${d.id}`}>{fmtBRL(d.valor)}</span>
        {pago ? (
          <Button size="sm" variant="ghost" disabled={busy} onClick={reabrir} data-testid={`despesa-reabrir-${d.id}`} title="Voltar para pendente">
            <Undo2 size={14} />
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setPagarOpen(true)}
            className="rounded-sm border-success/40 text-success hover:bg-success/10" data-testid={`despesa-pagar-${d.id}`}>
            <CheckCircle2 size={14} className="mr-1" /> Pagar
          </Button>
        )}
        {d.recorrencia_id && d.recorrencia_ativa && (
          <DesativarRecorrenciaButton recorrenciaId={d.recorrencia_id} descricao={d.descricao} onDone={onChanged}
            testId={`despesa-desativar-rec-${d.id}`} compact />
        )}
        <Button size="icon" variant="ghost" onClick={() => onDelete(d.id)} data-testid={`del-desp-${d.id}`}>
          <Trash2 size={14} className="text-destructive" />
        </Button>
      </div>

      <Dialog open={pagarOpen} onOpenChange={setPagarOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>Registrar pagamento</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">{d.descricao} — <b className="text-foreground">{fmtBRL(d.valor)}</b></div>
          <div>
            <Label className="label-tag">Data do pagamento</Label>
            <Input type="date" value={dataPag} onChange={(e) => setDataPag(e.target.value)} data-testid="despesa-pagar-data" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPagarOpen(false)}>Cancelar</Button>
            <Button disabled={busy || !dataPag} onClick={pagar} data-testid="despesa-pagar-confirmar"
              className="bg-foreground text-background hover:bg-foreground/90">Confirmar pagamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
